import React from 'react';
import { createPortal } from 'react-dom';

import type { ColumnConfig } from '../../hooks/useColumnManager';

interface DriveColumnSettingsMenuProps {
  theme: ReturnType<typeof import('../../contexts/ThemeContext').useTheme>['theme'];
  isOpen: boolean;
  position?: { x: number; y: number } | null;
  menuRef: React.RefObject<HTMLDivElement>;
  buttonRef: React.RefObject<HTMLButtonElement>;
  columns: ColumnConfig[];
  toggleColumnVisibility: (columnId: string) => void;
  resetToDefaults: () => void;
  onClose: () => void;
}

const DriveColumnSettingsMenu: React.FC<DriveColumnSettingsMenuProps> = ({
  theme,
  isOpen,
  position,
  menuRef,
  buttonRef,
  columns,
  toggleColumnVisibility,
  resetToDefaults,
  onClose,
}) => {
  if (!isOpen) {
    return null;
  }

  const style = (() => {
    if (position) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const menuWidth = 320;
      const menuHeight = 400;
      const margin = 8;
      let left = position.x;
      if (left + menuWidth + margin > viewportWidth) {
        left = viewportWidth - menuWidth - margin;
      }
      if (left < margin) {
        left = margin;
      }

      const top = position.y;
      const spaceBelow = viewportHeight - top;
      const spaceAbove = top;
      if (spaceBelow < menuHeight + margin && spaceAbove > spaceBelow) {
        return {
          bottom: `${viewportHeight - top + margin}px`,
          left: `${left}px`,
        };
      }

      return {
        top: `${top + margin}px`,
        left: `${left}px`,
      };
    }

    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const viewportHeight = window.innerHeight;
      const estimatedHeight = 400;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      const left = rect.left < 8 ? 8 : rect.left;

      if (shouldOpenUp) {
        return {
          bottom: `${viewportHeight - rect.top + 8}px`,
          left: `${left}px`,
        };
      }

      return {
        top: `${rect.bottom + 8}px`,
        left: `${left}px`,
      };
    }

    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  })();

  return createPortal(
    <div
      ref={menuRef}
      className={`fixed ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg shadow-xl z-[9999] py-2 min-w-[280px] max-w-[320px] max-h-[400px] overflow-y-auto scrollbar-thin`}
      style={style}
    >
      <div className="px-3 py-2 border-b flex items-center justify-between">
        <span className={`text-xs font-semibold ${theme.colors.text.primary}`}>
          Columnas visibles
        </span>
      </div>
      <div className="px-2 py-1 space-y-1">
        {[...columns]
          .filter((column) => !column.alwaysVisible) // Excluir columnas que siempre están visibles (checkbox, menu)
          .sort((a, b) => a.order - b.order)
          .map((column) => (
            <label
              key={column.id}
              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-opacity-50 py-1 px-1 rounded"
            >
              <input
                type="checkbox"
                checked={column.visible}
                onChange={() => toggleColumnVisibility(column.id)}
                className="h-4 w-4"
              />
              <span className={`flex-1 ${theme.colors.text.primary}`}>
                {column.label || column.id}
              </span>
            </label>
          ))}
      </div>
      <div className="px-3 py-2 border-t space-y-2">
        <p className={`text-xs ${theme.colors.text.secondary} text-center`}>
          Arrastrar para reorganizar
        </p>
        <button
          onClick={() => {
            resetToDefaults();
            onClose();
          }}
          className={`w-full px-3 py-2 rounded-md ${theme.colors.bg.tertiary} ${theme.colors.text.primary} hover:${theme.colors.bg.input} transition-colors text-sm font-medium`}
        >
          Restaurar por defecto
        </button>
      </div>
    </div>,
    document.body
  );
};

export default DriveColumnSettingsMenu;
